import type firebase from "firebase";
import { type GridFilterModel } from "@material-ui/x-grid";

import { calendarYmdInTimeZone } from "src/utils/timezone";

// vim-highlight-fix = }

export const DefaultFilterModel = { items: [] };

function normalizeString(str: string | null | undefined) {
  if (!str) str = "";
  if (str.trim) str = str.trim();
  if (str.toLowerCase) str = str.toLowerCase();
  return str;
}

const StringOperators = {
  contains: (item: string = "", value: string = ""): boolean => {
    item = normalizeString(item);
    if (!item.includes) return false;
    return item.includes(normalizeString(value));
  },
  equals: (item: string = "", value: string = ""): boolean => {
    return normalizeString(item) === normalizeString(value);
  },
  notEquals: (item: string = "", value: string = ""): boolean => {
    return normalizeString(item) !== normalizeString(value);
  },
  startsWith: (item: string = "", value: string = ""): boolean => {
    item = normalizeString(item);
    if (!item.startsWith) return false;
    return item.startsWith(normalizeString(value));
  },
  endsWith: (item: string = "", value: string = ""): boolean => {
    item = normalizeString(item);
    if (!item.endsWith) return false;
    return item.endsWith(normalizeString(value));
  },
  isEmpty: (item: string = ""): boolean => {
    if (item?.trim) item = item.trim();
    return !item;
  },
  isNotEmpty: (item: string = ""): boolean => {
    if (item?.trim) item = item.trim();
    return !!item;
  },
};

const ArrayStringOperators = {
  contains: (item: string[] = [], value: string = ""): boolean => {
    if (!item || !item?.some) return false;
    const v = normalizeString(value);
    return item.some((i) => {
      i = normalizeString(i);
      if (!i.includes) return false;
      return i.includes(v);
    });
  },
  equals: (item: string[] = [], value: string = ""): boolean => {
    if (!item) return false;
    return (
      item.length === 1 && normalizeString(item[0]) === normalizeString(value)
    );
  },
  startsWith: (item: string[] = [], value: string = ""): boolean => {
    if (!item || !item?.some) return false;
    const v = normalizeString(value);
    return item.some((i) => {
      i = normalizeString(i);
      if (!i.startsWith) return false;
      return i.startsWith(v);
    });
  },
  endsWith: (item: string[] = [], value: string = ""): boolean => {
    if (!item || !item.some) return false;
    const v = normalizeString(value);
    return item.some((i) => {
      i = normalizeString(i);
      if (!i.endsWith) return false;
      return i.endsWith(v);
    });
  },
  isEmpty: (item: any[] = []): boolean => {
    if (!item || item.length === 0) return true;
    if (item.length > 1) return false;
    if (item[0]) {
      if (item[0].trim) return !item[0].trim();
      return false;
    }
    return false;
  },
  isNotEmpty: (item: string[] = []): boolean => {
    if (!item) return false;
    if (!item.length) return false;
    let s = item[0];
    if (s.trim) s = s.trim();
    return !!s;
  },
};

const NumberOperators = {
  "=": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item === +value;
  },
  "!=": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item !== +value;
  },
  ">": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item > +value;
  },
  ">=": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item >= +value;
  },
  "<": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item < +value;
  },
  "<=": (item: number, value: number | undefined): boolean => {
    return value === undefined || +item <= +value;
  },
  isEmpty: (item: number): boolean => {
    return !item;
  },
  isNotEmpty: (item: number): boolean => {
    return !!item;
  },
};

const BooleanOperators = {
  is: (item: boolean, value: "false" | "true" | undefined): boolean => {
    const v = value === "true";
    return value === undefined || !!item === v;
  },
  isStrict: (item: boolean, value: "false" | "true" | undefined): boolean => {
    const v = value === "true";
    return item === v;
  },
};

const YMD_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalizes a row date to `yyyy-mm-dd` for filter comparison.
 * When `timeZone` is set, that calendar day is in the user's profile zone
 * (must match how filter `value` strings are built). Otherwise uses the
 * browser's local calendar (legacy).
 */
function getDateYmdForFilter(
  date: Date | string | firebase.firestore.Timestamp | null | undefined,
  timeZone?: string
): string {
  if (date == null) return "";
  if (typeof date === "string") {
    const s = date.trim();
    if (YMD_ONLY.test(s)) return s;
    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return "Invalid Date";
    date = parsed;
  } else if ((date as firebase.firestore.Timestamp).toDate) {
    date = (date as firebase.firestore.Timestamp).toDate();
  }

  const d = date as Date;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "Invalid Date";

  if (timeZone) {
    return calendarYmdInTimeZone(d, timeZone);
  }

  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(
    2,
    "0"
  )}-${`${d.getDate()}`.padStart(2, "0")}`;
}

function buildDateOperators(timeZone?: string) {
  const ymd = (item: unknown) =>
    getDateYmdForFilter(
      item as Date | string | firebase.firestore.Timestamp | null | undefined,
      timeZone
    );

  return {
    is: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) === value;
    },
    not: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) !== value;
    },
    after: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) > value;
    },
    onOrAfter: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) >= value;
    },
    before: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) < value;
    },
    onOrBefore: (item: unknown, value: string | undefined): boolean => {
      return !value || ymd(item) <= value;
    },
    isEmpty: (item: unknown): boolean => {
      return !item;
    },
    isNotEmpty: (item: unknown): boolean => {
      return !!item;
    },
  };
}

const DateOperators = buildDateOperators(undefined);

const Operators = {
  string: StringOperators,
  array_string: ArrayStringOperators,
  number: NumberOperators,
  boolean: BooleanOperators,
  date: DateOperators,
};

const LinkOperators = {
  or: (a: boolean, b: boolean): boolean => {
    return a || b;
  },
  and: (a: boolean, b: boolean): boolean => {
    return a && b;
  },
};

interface FilterDefinition<T> {
  operator: keyof typeof Operators;
  column: keyof T;
}

export interface FilterOptions {
  /** When set, date filters compare `yyyy-mm-dd` in this IANA zone (e.g. profile TZ). */
  dateComparisonTimeZone?: string;
}

export function filter<T>(
  definitions: Partial<
    Record<keyof T, keyof typeof Operators | FilterDefinition<T>>
  >,
  rows: T[],
  model: GridFilterModel,
  options?: FilterOptions
): T[] {
  const link = model.linkOperator || "and";
  if (!model || !model.items || model.items.length === 0) return rows;
  return rows.filter((row) => {
    let cond = link === "and" ? true : false;
    for (const item of model.items) {
      if (!item.columnField) return true;
      const t = definitions[item.columnField];
      if (!t) {
        console.warn("No definition for columnField", definitions, item);
        return true;
      }
      const typeKey =
        typeof t === "object" && t != null && "operator" in t ? t.operator : t;
      const operators =
        typeKey === "date"
          ? buildDateOperators(options?.dateComparisonTimeZone)
          : Operators[typeKey as keyof typeof Operators];
      if (!operators || !item.operatorValue) {
        console.warn("No operator for type definition", definitions, item);
        return true;
      }
      const cmp = operators[item.operatorValue];
      if (!cmp) {
        console.warn("No cmp function for type definition", definitions, item);
        return true;
      }
      cond = LinkOperators[link](
        cond,
        cmp(row[t.column || item.columnField], item.value)
      );
      switch (link) {
        case "and":
          if (!cond) return false;
          break;

        case "or":
          if (cond) return true;
          break;
      }
    }

    return cond;
  });
}
